import { getWindow, toValidNumber } from '@ztechapps/common.utils'

// Client-visible app config, resolved once at module load — from `process.env`
// on the server pass and from `window.__ENV__` on the client pass — so callers
// (StoreBadges, PricingSection) read one flat object instead of a context.
// `import.meta.env.SSR` is a build-time constant Vite statically replaces per
// build target, so the unused branch is dead-code-eliminated: `process.env`
// never reaches the client bundle. entry-server.tsx imports this same
// `appSettings` to serialise into window.__ENV__, so the SSR render and the
// client hydration read from one shared computation and never disagree.
export type RuntimeAppEnv = {
  appStoreUrl?: string
  playStoreUrl?: string
  pesoPerSheet?: number
  // Public QuickScore API origin — the support area's browser-side Apollo client, sign-in
  // forms and file-upload picker all call this directly (unlike the admin app, this website's
  // own Express server proxies no /graphql or /auth route in production).
  apiUrl?: string
  // Google reCAPTCHA v3 site key — required by the /auth/passwordless/quickscore-website and
  // /auth/basic/quickscore-website mounts (WebsiteRouter.ts), which inherit BasicAuthRouter's /
  // PasswordlessAuthRouter's recaptchaTokenMiddleware unchanged (unlike the JSON-only mobile
  // mounts, which drop it). Same site key as apps/quickscore/admin/.env* — recaptcha site keys
  // are public by design; only the paired secret key stays server-only.
  recaptchaSiteKey?: string
  // Public QuickScore API origin for the anonymous help-pages Apollo client (see
  // pages/help/HelpApolloProvider.tsx) — PUBLIC_API_URL in apps/quickscore/website/.env*.
  publicApiUrl?: string
}
export type RuntimeEnv = {
  app?: RuntimeAppEnv
}

export const appSettings: RuntimeAppEnv = import.meta.env.SSR
  ? {
      appStoreUrl: process.env.APP_STORE_URL,
      playStoreUrl: process.env.PLAY_STORE_URL,
      pesoPerSheet: toValidNumber(process.env.PESO_PER_SHEET),
      apiUrl: process.env.VITE_APP_API_URL,
      recaptchaSiteKey: process.env.VITE_GOOGLE_RECAPTCHA_SITEKEYS,
      publicApiUrl: process.env.PUBLIC_API_URL,
    }
  : ((getWindow() as unknown as { __ENV__?: RuntimeEnv }).__ENV__?.app ?? {})

// Matches the shared useUser/useLoginFlow default (libs/common/hook/src/useUser/index.ts) — the
// support sign-in flow writes here directly (see pages/support/session.ts) so both it and any
// future admin-style login share the same localStorage keys.
export const supportUserStorageKeys = {
  userId: 'app-login-userId',
  token: 'app-login-token',
}

// Carries the captured return path across the passwordless sign-in leg, which has no query-string
// channel back to the server (SupportSignInPage.tsx reads/writes it, auth-success/index.tsx reads
// and clears it). sessionStorage, not localStorage — the value is single-use, scoped to the tab
// that started this sign-in, and must not outlive it.
export const supportRedirectUrlStorageKey = 'support-redirect-url'
